import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { elitePulseFetch } from '../../utils/elitePulseFetch';
import { isDesignSystemOn } from '../../theme/mmecDsPreview';
import {
  buildElitePulseReportNotifyPayload,
  ELITE_PULSE_NEW_REPORT_EVENT,
  requestBrowserNotificationPermission,
  sendElitePulseBrowserNotification,
  type ElitePulseReportNotifyPayload,
} from '../../utils/elitePulseReportNotify';
import {
  ELITE_PULSE_NOTIFY_CHANGED,
  loadElitePulseNotifySettings,
  loadLastNotifiedFingerprint,
  saveElitePulseNotifySettings,
  saveLastNotifiedFingerprint,
  type ElitePulseNotifySettings,
} from '../../utils/elitePulseNotificationStore';

const POLL_MS = 60_000;

function formatGeneratedAt(iso: string, language: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(language === 'ar' ? 'ar' : 'en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

const ElitePulseReportNotification: React.FC = () => {
  const { language } = useLanguage();
  const [settings, setSettings] = useState<ElitePulseNotifySettings>(() => loadElitePulseNotifySettings());
  const [active, setActive] = useState<ElitePulseReportNotifyPayload | null>(null);
  const seededRef = useRef(false);
  const checkingRef = useRef(false);

  useEffect(() => {
    const sync = () => setSettings(loadElitePulseNotifySettings());
    window.addEventListener(ELITE_PULSE_NOTIFY_CHANGED, sync);
    return () => window.removeEventListener(ELITE_PULSE_NOTIFY_CHANGED, sync);
  }, []);

  useEffect(() => {
    if (settings.enabled && settings.browserPushEnabled) {
      requestBrowserNotificationPermission();
    }
  }, [settings.enabled, settings.browserPushEnabled]);

  const emitOpen = useCallback((payload: ElitePulseReportNotifyPayload) => {
    window.dispatchEvent(
      new CustomEvent(ELITE_PULSE_NEW_REPORT_EVENT, {
        detail: { ...payload, openChat: true, navigate: true },
      }),
    );
    const simple =
      typeof document !== 'undefined' &&
      document.querySelector('.mmec-simple-shell') != null;
    if (!simple) {
      window.dispatchEvent(
        new CustomEvent('navigate-to-tab', { detail: { tab: 'elite-pulse' } }),
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('mmec-simple-navigate-tab', { detail: { tab: 'elite_pulse' } }),
      );
    }
  }, []);

  const dismiss = useCallback(() => setActive(null), []);

  const checkForNewReport = useCallback(async () => {
    if (!settings.enabled || checkingRef.current) return;
    checkingRef.current = true;
    try {
      const [viewRes, bundleRes] = await Promise.all([
        elitePulseFetch('/api/ois/report-view'),
        elitePulseFetch('/api/ois/intel/bundle'),
      ]);
      const viewJson = viewRes.ok ? await viewRes.json().catch(() => ({})) : {};
      const bundleJson = bundleRes.ok ? await bundleRes.json().catch(() => ({})) : {};

      const rawReport =
        viewJson?.success && viewJson.report != null ? viewJson.report : null;
      const summaryJson =
        bundleJson?.success && bundleJson.summary_json != null
          ? bundleJson.summary_json
          : null;

      const payload = buildElitePulseReportNotifyPayload(rawReport, summaryJson, 'ar');
      if (!payload) return;

      const lastFp = loadLastNotifiedFingerprint();

      if (!seededRef.current) {
        seededRef.current = true;
        if (!lastFp) {
          saveLastNotifiedFingerprint(payload.fingerprint);
          return;
        }
      }

      if (payload.fingerprint === lastFp) return;

      saveLastNotifiedFingerprint(payload.fingerprint);
      setActive(payload);

      if (settings.browserPushEnabled) {
        sendElitePulseBrowserNotification(payload, 'ar');
      }

      window.dispatchEvent(
        new CustomEvent(ELITE_PULSE_NEW_REPORT_EVENT, {
          detail: { ...payload, openChat: false, navigate: false },
        }),
      );
    } catch (err) {
      console.warn('[ElitePulseNotify] poll failed:', err);
    } finally {
      checkingRef.current = false;
    }
  }, [language, settings.browserPushEnabled, settings.enabled]);

  useEffect(() => {
    if (!settings.enabled) return;
    void checkForNewReport();
    const id = window.setInterval(() => void checkForNewReport(), POLL_MS);
    return () => clearInterval(id);
  }, [checkForNewReport, settings.enabled]);

  const toggleEnabled = () => {
    const next = { ...settings, enabled: !settings.enabled };
    setSettings(next);
    saveElitePulseNotifySettings(next);
    if (!next.enabled) setActive(null);
  };

  const toggleBrowserPush = () => {
    const next = { ...settings, browserPushEnabled: !settings.browserPushEnabled };
    setSettings(next);
    saveElitePulseNotifySettings(next);
    if (next.browserPushEnabled) requestBrowserNotificationPermission();
  };

  if (!settings.enabled || !active) {
    return null;
  }

  /** Report alert copy defaults to Arabic regardless of UI language. */
  const ar = true;
  // Design System preview: MMEC.Toast look; "View report" is the one gold action.
  const ds = isDesignSystemOn();

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10001,
        maxWidth: '420px',
        width: 'min(420px, calc(100vw - 40px))',
      }}
    >
      <div
        style={{
          padding: ds ? '12px' : '16px',
          backgroundColor: 'var(--surface-panel, var(--elev))',
          color: 'var(--txt)',
          borderRadius: ds ? '4px' : '10px',
          boxShadow: 'var(--shadow-panel, var(--lift-3))',
          border: ds ? '1px solid var(--line)' : '1px solid var(--border)',
          borderInlineStart: ds ? '3px solid var(--gold)' : undefined,
          fontFamily: ds ? 'var(--font-arabic)' : 'var(--font-ui)',
          animation: 'elitePulseNotifyIn 0.35s ease-out',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: ds && ar ? 0 : '0.08em', textTransform: ds && ar ? 'none' : 'uppercase', color: 'var(--text-muted, var(--muted-2))', marginBottom: 4 }}>
              {ar ? 'نبض النخبة' : 'ELITE PULSE'}
            </div>
            <strong style={{ fontSize: ds ? 13 : 14, fontWeight: 600, color: ds ? 'var(--text)' : 'var(--acc)' }}>
              {ar ? 'تقرير جديد متاح' : 'New report available'}
            </strong>
            <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>
              {formatGeneratedAt(active.generatedAt, 'ar')}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={ar ? 'إغلاق' : 'Dismiss'}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              fontSize: 18,
              cursor: 'pointer',
              lineHeight: 1,
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        <p style={{ margin: '12px 0 8px', fontSize: 13, lineHeight: 1.45, direction: 'rtl', textAlign: 'right' }}>
          {ar ? 'أبرز الفرص في هذا التقرير:' : 'Latest opportunities in this report:'}
        </p>

        <ul style={{ margin: 0, padding: '0 18px 0 0', fontSize: 13, lineHeight: 1.5, direction: 'rtl', textAlign: 'right' }}>
          {active.previews.length > 0 ? (
            active.previews.map((p, i) => (
              <li key={`${p.kind}-${p.symbol}-${i}`} style={{ marginBottom: 8 }}>
                {p.symbol !== 'PULSE' ? (
                  <>
                    <strong>{p.symbol}</strong>
                    <span style={{ color: 'var(--txt-2)' }}> — {p.headline}</span>
                  </>
                ) : (
                  <span>{p.headline}</span>
                )}
                {p.detail ? (
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{p.detail}</div>
                ) : null}
              </li>
            ))
          ) : (
            <li style={{ listStyle: 'none', marginLeft: -18 }}>
              {ar
                ? 'افتح التقرير لعرض الفرص والجداول الكاملة.'
                : 'Open the report for full opportunities and tables.'}
            </li>
          )}
        </ul>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
          <button
            type="button"
            onClick={() => {
              emitOpen(active);
              dismiss();
            }}
            style={{
              flex: 1,
              minWidth: 120,
              height: 32,
              padding: '0 12px',
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: ds ? 'inherit' : 'var(--font-ui)',
              borderRadius: ds ? 4 : 8,
              border: 'none',
              cursor: 'pointer',
              backgroundColor: 'var(--gold, var(--acc))',
              color: 'var(--text-on-accent, var(--void))',
            }}
          >
            {ar ? 'عرض التقرير' : 'View report'}
          </button>
          <button
            type="button"
            onClick={() => {
              window.dispatchEvent(
                new CustomEvent(ELITE_PULSE_NEW_REPORT_EVENT, {
                  detail: { ...active, openChat: true, discussReport: true, navigate: false },
                }),
              );
              dismiss();
            }}
            style={{
              flex: 1,
              minWidth: 120,
              height: 32,
              padding: '0 12px',
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: ds ? 'inherit' : 'var(--font-ui)',
              borderRadius: ds ? 4 : 8,
              cursor: 'pointer',
              backgroundColor: 'var(--elev2)',
              color: 'var(--txt)',
              border: '1px solid var(--hair)',
            }}
          >
            {ar ? 'اسأل المساعد' : 'Ask AI'}
          </button>
        </div>

        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid var(--hair)',
            fontSize: 11,
            color: 'var(--muted)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.enabled} onChange={toggleEnabled} />
            {ar ? 'تنبيهات التقرير' : 'Report alerts'}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.browserPushEnabled} onChange={toggleBrowserPush} />
            {ar ? 'إشعار المتصفح' : 'Browser push'}
          </label>
        </div>
      </div>
      <style>{`
        @keyframes elitePulseNotifyIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ElitePulseReportNotification;
